import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import Logo from './Logo';
import { Brain, Menu, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Header: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Function to determine if a link is active
  const isActive = (path: string) => {
    if (path === '/' && currentPath === '/') return true;
    if (path !== '/' && currentPath.startsWith(path)) return true;
    return false;
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md">
      <div className="container mx-auto h-16 px-4 sm:px-6 lg:px-8 grid grid-cols-3 items-center">
        <div className="flex justify-start">
          <Logo size="md" className="scale-80 transform-gpu" light={false} />
        </div>
        <div className="flex justify-center">
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-base font-medium transition-colors relative ${
                isActive('/') 
                  ? 'text-roundone-purple' 
                  : 'hover:text-roundone-purple'
              }`}
            >
              Home
              {isActive('/') && (
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-roundone-purple rounded-full" />
              )}
            </Link>
            <Link 
              to="/pricing" 
              className={`text-base font-medium transition-colors relative ${
                isActive('/pricing') 
                  ? 'text-roundone-purple' 
                  : 'hover:text-roundone-purple'
              }`}
            >
              Pricing
              {isActive('/pricing') && (
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-roundone-purple rounded-full" />
              )}
            </Link>
            <Link 
              to="/ats-integrations" 
              className={`text-base font-medium transition-colors relative ${
                isActive('/ats-integrations') 
                  ? 'text-roundone-purple' 
                  : 'hover:text-roundone-purple'
              }`}
            >
              Integrations
              {isActive('/ats-integrations') && (
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-roundone-purple rounded-full" />
              )}
            </Link>
            <a 
              href="https://mock.roundone.ai?utm_source=roundone.ai&utm_medium=header&utm_campaign=practice"
              className="text-base font-medium transition-colors relative flex items-center gap-1 whitespace-nowrap hover:text-roundone-purple"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Brain size={16} className="flex-shrink-0" />
              <span>Practice</span>
              <span className="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0">New</span>
            </a>
          </nav>
        </div>
        <div className="flex justify-end gap-4 items-center">
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Menu className="h-5 w-5 text-gray-800" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[250px] sm:w-[300px]">
                <nav className="flex flex-col gap-4 mt-8">
                  <Link 
                    to="/" 
                    className={`text-base font-medium transition-colors py-2 ${
                      isActive('/') ? 'text-roundone-purple' : ''
                    }`}
                  >
                    Home
                  </Link>
                  <Link 
                    to="/pricing" 
                    className={`text-base font-medium transition-colors py-2 ${
                      isActive('/pricing') ? 'text-roundone-purple' : ''
                    }`}
                  >
                    Pricing
                  </Link>
                  <Link 
                    to="/ats-integrations" 
                    className={`text-base font-medium transition-colors py-2 ${
                      isActive('/ats-integrations') ? 'text-roundone-purple' : ''
                    }`}
                  >
                    Integrations
                  </Link>
                  <a 
                    href="https://mock.roundone.ai?utm_source=roundone.ai&utm_medium=header&utm_campaign=practice"
                    className="text-base font-medium transition-colors py-2 flex items-center gap-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Brain size={16} className="flex-shrink-0" />
                    <span>Practice</span>
                    <span className="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0">New</span>
                  </a>
                  <div className="flex flex-col gap-3 mt-4">
                    <Link to="/sign-in?tab=login" className="w-full">
                      <Button variant="light-ghost" size="sm" className="w-full">Log in</Button>
                    </Link>
                    <Link to="/sign-in?tab=register" className="w-full">
                      <Button className="gradient-button w-full" size="sm">Sign up free</Button>
                    </Link>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
          <div className="hidden md:flex gap-4">
            <Link to="/sign-in?tab=login">
              <Button variant="light-ghost" size="sm">Log in</Button>
            </Link>
            <Link to="/sign-in?tab=register">
              <Button className="gradient-button" size="sm">Sign up free</Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;